import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { birdToast } from '../../core-ui/toast';
import { isErrorInfo } from '../../../interfaces/error-info.interface';
import { getHrAttendance, toggleHrAttendance } from '../../../services/data/hr.data';
import { HrAttendanceUiModel } from '../../../ui-models/hr.ui-model';

/** How often the "worked today" figure is recomputed locally. No request is made for it. */
const TICK_MS = 60 * 1000;

const pad = (n: number) => String(n).padStart(2, '0');
const clock = (at: Date) => `${pad(at.getHours())}:${pad(at.getMinutes())}`;

export function formatWorked(seconds: number): string {
  const minutes = Math.max(0, Math.floor(seconds / 60));
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${hours}h ${pad(minutes % 60)}m` : `${minutes}m`;
}

/** Worked seconds now: the server's figure, plus the time since it was read while checked in. */
export function workedNow(status: HrAttendanceUiModel, now: Date): number {
  const since = status.checkedIn
    ? Math.max(0, Math.floor((now.getTime() - status.fetchedAt.getTime()) / 1000))
    : 0;
  return status.workedSeconds + since;
}

/**
 * Check-in / check-out (#699), next to the user in the side menu. Horilla is the record; this
 * reads the status once when it mounts and again from each toggle's answer, and never polls.
 *
 * Renders nothing until the status is known, and nothing at all for someone the BFF cannot
 * match to a Horilla employee (404 HR_NOT_LINKED) or when HR is unreachable: an attendance
 * button that cannot work is worse than none.
 */
export const AttendanceToggle: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<HrAttendanceUiModel | undefined>();
  const [pending, setPending] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    getHrAttendance().then((result) => {
      if (mounted.current && !isErrorInfo(result)) setStatus(result);
    });
    return () => {
      mounted.current = false;
    };
  }, []);

  const checkedIn = !!status?.checkedIn;
  useEffect(() => {
    if (!checkedIn) return undefined;
    const timer = window.setInterval(() => setNow(new Date()), TICK_MS);
    return () => window.clearInterval(timer);
  }, [checkedIn]);

  if (!status) return null;

  const toggle = () => {
    setPending(true);
    toggleHrAttendance(status.checkedIn ? 'clock-out' : 'clock-in').then((result) => {
      if (!mounted.current) return;
      setPending(false);
      if (isErrorInfo(result)) {
        birdToast.error(
          result.message || t('SHARED.ATTENDANCE.ERROR'),
          t('SHARED.ATTENDANCE.TOAST_TITLE')
        );
        return;
      }
      setStatus(result);
      setNow(new Date());
      birdToast.success(
        t(
          result.checkedIn
            ? 'SHARED.ATTENDANCE.CHECKED_IN_TOAST'
            : 'SHARED.ATTENDANCE.CHECKED_OUT_TOAST'
        ),
        t('SHARED.ATTENDANCE.TOAST_TITLE')
      );
    });
  };

  const worked = formatWorked(workedNow(status, now));

  return (
    <div className="flex items-center gap-2 px-2 pb-2" data-testid="attendance-toggle">
      <AccessTimeIcon
        className={status.checkedIn ? 'text-primary-500' : 'text-gray-400'}
        style={{ fontSize: 20 }}
      />
      <div className="flex-1 min-w-0">
        <span className="block text-sm font-gilroy-medium text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis">
          {status.checkedIn && status.clockInAt
            ? t('SHARED.ATTENDANCE.CHECKED_IN_SINCE', { time: clock(status.clockInAt) })
            : t('SHARED.ATTENDANCE.NOT_CHECKED_IN')}
        </span>
        <span className="block text-xs font-gilroy-medium text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
          {t('SHARED.ATTENDANCE.WORKED_TODAY', { duration: worked })}
        </span>
      </div>
      <button
        type="button"
        disabled={pending}
        aria-busy={pending}
        onClick={toggle}
        className={`shrink-0 h-9 px-3 rounded-xl text-sm font-gilroy-medium whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:opacity-60 disabled:cursor-not-allowed ${
          status.checkedIn
            ? 'border border-gray-200 bg-white text-gray-700 hover:border-gray-400'
            : 'bg-primary-500 text-white hover:bg-primary-600'
        }`}
      >
        {status.checkedIn ? t('SHARED.ATTENDANCE.CHECK_OUT') : t('SHARED.ATTENDANCE.CHECK_IN')}
      </button>
    </div>
  );
};
